import {z} from 'zod'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Switch} from '@/components/commons/switch/Switch'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {useTranslation} from '@/translations/i18n'
import {InputText} from '@/components/commons/input-text/InputText'
import {MedicalReportFieldsSchema} from '../../formSchemas'
import {housingConditionMapToLabel} from '../../utils'
import {SwitchField} from '../switch-field/SwitchField'

const HousingConditionSchema = MedicalReportFieldsSchema.pick({
    houseType: true,
    houseTypeOther: true,
    hasArchitecturalBarriers: true,
    architecturalBarriers: true
})
type HousingConditionSchema = z.infer<typeof HousingConditionSchema>

export const HousingConditionFields = () => {
    const {t} = useTranslation()
    const form = useFormContext<HousingConditionSchema>()

    const houseType = useWatch({control: form.control, name: 'houseType'})
    const hasArchitecturalBarriers = useWatch({control: form.control, name: 'hasArchitecturalBarriers'})

    return (
        <Flexbox direction="column" gap={4}>
            <Grid columns={3} gap={4} fullWidth>
                <GridItem spanColumn={1}>
                    <Controller
                        control={form.control}
                        name="houseType"
                        render={({field}) => (
                            <InputSelect
                                label={t('medical_records:house')}
                                optionsMap={housingConditionMapToLabel}
                                value={field.value}
                                onChange={value => {
                                    field.onChange(value)
                                    form.setValue('houseTypeOther', '')
                                }}
                            />
                        )}
                    />
                </GridItem>
                {houseType == 'other' && (
                    <GridItem spanColumn={1}>
                        <InputText
                            type="text"
                            label={t('commons:other')}
                            placeholder={t('medical_records:insert_info')}
                            {...form.register('houseTypeOther')}
                        />
                    </GridItem>
                )}
            </Grid>
            <SwitchField columns={3} gap={4} fullWidth height="58px">
                <GridItem spanColumn={1}>
                    <Controller
                        control={form.control}
                        name="hasArchitecturalBarriers"
                        render={({field}) => (
                            <Switch
                                size="sm"
                                label={t('medical_records:has_architectural_barriers')}
                                defaultChecked={field.value}
                                onChange={value => {
                                    field.onChange(value)
                                    form.setValue('architecturalBarriers', '')
                                }}
                            />
                        )}
                    />
                </GridItem>
                {hasArchitecturalBarriers && (
                    <Controller
                        name="architecturalBarriers"
                        control={form.control}
                        render={({field}) => (
                            <InputSelect
                                label="Tipo barriere architettoniche"
                                optionsMap={housingConditionMapToLabel}
                                placeholder={t('medical_records:select_an_option_placeholder')}
                                onChange={field.onChange}
                                value={field.value}
                            />
                        )}
                    />
                )}
            </SwitchField>
        </Flexbox>
    )
}
